/**
 * The ground — how the real room is treated before anything is drawn on it.
 *
 * Not "a filter" in the photo-app sense, and deliberately not black and white.
 * Full greyscale is the most obvious AR move, reads as a lens rather than a
 * work, and would leave gold as the only colour on screen, which either screams
 * or turns gimmicky. Distortion (glitch, chromatic aberration, warping) was
 * rejected for a different reason: it says *digital malfunction*, and this piece
 * is arguing presence and belief, not breakage. The PS1 layer already supplies
 * the digital register as a rendering convention, which is the right dose.
 *
 * What this does instead is three ops that make the room look like an old
 * painted panel darkened with age: drain most of the colour, pull the blacks
 * toward brown, and lower highlight contrast so the world sits BEHIND the marks
 * rather than competing with them. The point clouds then hold the only real
 * light in the frame, which is exactly the icon relationship — aged tempera
 * ground, gold that still catches.
 *
 * ── Why it costs the tracker nothing ──────────────────────────────────────
 * This is a CSS `filter` on the <video> element. A CSS filter is a paint-time
 * compositing effect; `ctx.drawImage(video, …)` samples the decoded frame and
 * bypasses CSS entirely. Both the tracker (`VideoFrameProvider.drawTo`) and the
 * Watcher (`captureFrame`) therefore keep receiving pristine pixels no matter
 * what the participant sees. That is a guarantee of how the two APIs are
 * specified, not a workaround.
 *
 * ⚠️ Two consequences worth knowing:
 *  - The Watcher describes the UNFILTERED room. Better for recognition, and a
 *    deliberate dissonance: it sees through the filter it gave you.
 *  - Under WebXR there is no <video> element (the XR compositor is the
 *    passthrough), so this does nothing in that mode.
 *
 * `blur()` is avoided throughout: it is the one filter that is a real
 * convolution rather than a per-pixel op, and full-screen on mobile it is
 * expensive. Everything used here is effectively free.
 */

import type { RegimeName } from '@wintermute/orchestrator';

export interface Ground {
  saturate: number;
  contrast: number;
  brightness: number;
  sepia: number;
  /** Degrees. Small negative values cool the image without reading as a tint. */
  hueRotate: number;
}

/** The aged panel. Every regime is a departure from this, not a separate look. */
const BASE: Ground = {
  saturate: 0.34,
  contrast: 0.94,
  brightness: 0.86,
  sepia: 0.2,
  hueRotate: 0,
};

/**
 * Per-regime treatment.
 *
 * This is the part that makes the ground worth having: it is not decoration but
 * the work's mood made environmental. Comfort warms and softens the room;
 * crisis-swarm cools and hardens it; memory-vault sinks it deepest into sepia.
 * WinterMute sets the tone, and the room agrees with it.
 *
 * Kept subtle on purpose. The test is that nobody should say "nice filter" —
 * they should say the room looks wrong.
 */
const BY_REGIME: Record<RegimeName, Partial<Ground>> = {
  neutral: {},
  comfort: { saturate: 0.46, contrast: 0.86, brightness: 0.95, sepia: 0.32 },
  surprise: { saturate: 0.24, contrast: 1.14, brightness: 0.8, hueRotate: -8 },
  'echo-well': { saturate: 0.3, contrast: 0.98, brightness: 0.84, sepia: 0.26 },
  'crisis-swarm': { saturate: 0.2, contrast: 1.22, brightness: 0.74, hueRotate: -12 },
  'memory-vault': { saturate: 0.5, contrast: 0.82, brightness: 0.97, sepia: 0.5 },
};

export function groundFor(regime: RegimeName): Ground {
  return { ...BASE, ...(BY_REGIME[regime] ?? {}) };
}

/**
 * A CSS `filter` value. Order matters: sepia before hue-rotate reads warmer.
 *
 * When disabled this returns the IDENTITY filter list rather than `none`.
 * Transitioning a filter list to `none` is under-specified in practice — engines
 * disagree about whether the endpoints are interpolable — so a 2.4 s transition
 * to `none` can snap, stall, or animate, depending on the browser. Two lists of
 * the same functions always interpolate, so the room fades between treatments
 * instead of cutting.
 */
export function groundCss(regime: RegimeName, enabled: boolean): string {
  if (!enabled) return 'saturate(1) sepia(0) contrast(1) brightness(1)';
  const g = groundFor(regime);
  return [
    `saturate(${g.saturate})`,
    `sepia(${g.sepia})`,
    g.hueRotate ? `hue-rotate(${g.hueRotate}deg)` : '',
    `contrast(${g.contrast})`,
    `brightness(${g.brightness})`,
  ]
    .filter(Boolean)
    .join(' ');
}
